/**
 * Een verhaal in vijf deelteksten maken of bewerken, in het tabblad Teksten
 * (de knop Nieuwe fictionele tekst). Sinds stap 20 één formulier: met een
 * tijdvak en jaartal is het historische fictie (stap 17), zonder speelt het
 * nu (fictie van nu, stap 17d). Dezelfde motor: Greimas en Propp, de
 * vertelkunst, het vertelplan, twee bouwstenen per deeltekst ([1A] ...
 * [5B]). Het genre volgt het tijdvak (genreBij); het verhaal hieronder
 * onthoudt in welk genre het geschreven is, en opslaan kan pas als dat nog
 * klopt (van nu naar toen, of terug, is opnieuw schrijven).
 *
 * Bovenaan wat de beheerder kiest: het tijdvak (mag leeg), de plaats, de sociale
 * situatie, de themawoorden, een plotsjabloon, het vertelperspectief, extra
 * wensen, en eventueel twee of drie karakters (naam, rol, beschrijving).
 * "Naar het voorbeeld van" maakt het verhaal een tweede versie van een
 * ander verhaal, in een andere tijd: het sjabloon is dan dat van het
 * voorbeeld, en de karakters (met hun namen) worden overgenomen. Het model
 * zet per bouwsteen op wat er in de twee versies anders is.
 *
 * "Schrijf het verhaal" laat het model schrijven; er wordt dan nog niets
 * bewaard. "Laat de geschiedenis nakijken" (alleen historische fictie, stap
 * 17c) laat het model het verhaal nog eens lezen als historicus; wat het
 * vindt, staat onder de deelteksten en gaat bij opslaan mee in de opzet.
 *
 * Daaronder het verhaal: de titel, de vijf deelteksten (te bewerken, met de
 * markeringen erin), wat de app nakeek, en de opzet (components/
 * Verhaalopzet.js). Opslaan kan pas als elke deeltekst zijn twee markeringen
 * heeft. Tijdvak, plaats, situatie en themawoorden gaan bij opslaan mee in
 * de opzet; perspectief en karakters horen bij het verhaal zoals het
 * geschreven is, en een ander plotsjabloon kan pas met een nieuw verhaal.
 *
 * Bij fictie van nu kan de beheerder ook een bestaand verhaal plakken en
 * naar 3F laten omzetten, zonder deelteksten (onPlakken: het gewone
 * formulier van de fictionele tekst).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Check, Plus, RefreshCw, Search, Sparkles, X } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { NakijkPunten, PERSPECTIEVEN, Verhaalopzet } from '../../../components/Verhaalopzet';
import { FICTIE_VAN_NU, HISTORISCHE_FICTIE, VERHAALGENRES } from '../../../keuzelijsten';

const MARKERING = /\[(\d)([AB])\]/g;
const WOORDEN_PER_DEEL = [150, 190];   // zoals WOORDEN_PER_DEEL_3F; de server geeft het ook mee
const THEMAWOORDEN = [5, 8];   // zoals THEMAWOORDEN in services/historische_fictie.py

/** De markeringen in een deeltekst: ["1A", "1B"]. */
export function markeringenIn(tekst) {
  return [...(tekst || '').matchAll(MARKERING)].map(m => `${m[1]}${m[2]}`);
}

/** Het aantal woorden zonder de markeringen. */
export function woordenIn(tekst) {
  return (tekst || '').replace(MARKERING, ' ').trim().split(/\s+/).filter(Boolean).length;
}

/** Wat er mis is met de markeringen van deeltekst `nummer` (leeg: niets). */
export function markeringProbleem(tekst, nummer) {
  const gevonden = markeringenIn(tekst);
  const nodig = [`${nummer}A`, `${nummer}B`];
  const ontbreekt = nodig.filter(m => !gevonden.includes(m));
  const vreemd = gevonden.filter(m => !nodig.includes(m));
  if (ontbreekt.length) return `mist ${ontbreekt.map(m => `[${m}]`).join(' en ')}`;
  if (vreemd.length) return `heeft ${vreemd.map(m => `[${m}]`).join(', ')}, die hier niet hoort`;
  if (nodig.some(m => gevonden.filter(g => g === m).length > 1)) return 'heeft een markering twee keer';
  return '';
}

const LEGE_INVOER = { tijdvak: '', plaats: '', situatie: '', themawoorden: '', sjabloon: 1, extra: '', perspectief: 'ik',
  karakters: [], voorbeeldId: '' };
export const KARAKTERROLLEN = ['hoofdpersoon', 'helper', 'tegenstander', 'ander'];   // zoals in services/historische_fictie.py
const MAX_KARAKTERS = 3;
const LEEG_KARAKTER = { naam: '', rol: 'hoofdpersoon', beschrijving: '' };

const kopie = (karakters) => (karakters || []).map(k => ({ naam: k.naam || '', rol: k.rol || 'ander', beschrijving: k.beschrijving || '' }));

/** De invoer uit een opgeslagen opzet. */
const invoerVan = (opzet) => (opzet ? {
  tijdvak: opzet.tijdvak || '', plaats: opzet.plaats || '', situatie: opzet.situatie || '',
  themawoorden: (opzet.themawoorden || []).join(', '), sjabloon: opzet.sjabloon || 1, extra: '',
  perspectief: opzet.perspectief || 'ik', karakters: kopie(opzet.karakters), voorbeeldId: opzet.voorbeeld?.id || '',
} : LEGE_INVOER);

const genreNaam = (genre) => (VERHAALGENRES.find(g => g.value === genre)?.label || '').toLowerCase();

/** Het genre bij een tijdvak: ingevuld is historische fictie, leeg een verhaal van nu. */
export const genreBij = (tijdvak) => ((tijdvak || '').trim() ? HISTORISCHE_FICTIE.value : FICTIE_VAN_NU.value);

export function VerhaalFormulier({ bestaand, verhalen = [], onOpslaan, onAnnuleer, onPlakken, meldFout }) {
  const [sjablonen, setSjablonen] = useState([]);
  const [woordenPerDeel, setWoordenPerDeel] = useState(WOORDEN_PER_DEEL);
  const [invoer, setInvoer] = useState(invoerVan(bestaand?.opzet));
  // het verhaal hieronder, met het genre waarin het geschreven is
  const [verhaal, setVerhaal] = useState(bestaand ? {
    titel: bestaand.titel, beschrijving: bestaand.beschrijving || '', opzet: bestaand.opzet, genre: bestaand.genre,
    deelteksten: bestaand.paragraphs.map(p => p.gemarkeerd || p.inhoud), controle: null,
  } : null);
  const genre = genreBij(invoer.tijdvak);                 // het genre van wat er nu ingevuld is
  const historisch = genre === HISTORISCHE_FICTIE.value;
  const anderGenre = Boolean(verhaal?.genre) && verhaal.genre !== genre;
  const [schrijven, setSchrijven] = useState(false);
  const [melding, setMelding] = useState('');
  const [opslaan, setOpslaan] = useState(false);
  const [nakijken, setNakijken] = useState(false);
  const [nakijkMelding, setNakijkMelding] = useState('');
  const [voorbeeld, setVoorbeeld] = useState(null);     // het voorbeeld, zoals de server het gaf (bij "Naar het voorbeeld van")
  const gevraagdVoorbeeld = useRef('');                  // het laatst gekozen voorbeeld: een later antwoord voor een ander telt niet
  // Het voorbeeld waarnaar het verhaal hieronder geschreven is ('' als het er geen heeft, of als het niet meer bestaat).
  const geschrevenNaar = (() => {
    const id = verhaal?.opzet?.voorbeeld?.id || '';
    return id && verhalen.some(v => v.id === id) ? id : '';
  })();

  useEffect(() => {
    api.haalHistorischeSjablonen().then(r => {
      setSjablonen(r.sjablonen);
      if (Array.isArray(r.woorden_per_deel) && r.woorden_per_deel.length === 2) setWoordenPerDeel(r.woorden_per_deel);
    }).catch(err => meldFout(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen
  }, []);

  // Een bestaand verhaal dat naar een voorbeeld geschreven is: de opzet van dat voorbeeld erbij (voor het sjabloon).
  useEffect(() => {
    const id = bestaand?.opzet?.voorbeeld?.id;
    if (!id) return;
    if (!verhalen.some(v => v.id === id)) {
      setInvoer(v => ({ ...v, voorbeeldId: '' }));   // het voorbeeld is er niet meer
      return;
    }
    gevraagdVoorbeeld.current = id;
    api.haalTekst(id).then(d => { if (gevraagdVoorbeeld.current === id) setVoorbeeld(d); })
      .catch(err => meldFout(`Het voorbeeld kon niet worden geladen: ${err.message}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen
  }, []);

  const zet = (veld) => (e) => setInvoer({ ...invoer, [veld]: e.target.value });

  const zetKarakter = (i, veld) => (e) => setInvoer(v => ({
    ...v, karakters: v.karakters.map((k, j) => (j === i ? { ...k, [veld]: e.target.value } : k)) }));
  const voegKarakterToe = () => setInvoer(v => ({
    ...v, karakters: [...v.karakters, { ...LEEG_KARAKTER, rol: v.karakters.length ? 'helper' : 'hoofdpersoon' }] }));
  const haalKarakterWeg = (i) => setInvoer(v => ({ ...v, karakters: v.karakters.filter((_, j) => j !== i) }));

  /**
   * Een voorbeeld kiezen: het sjabloon, het perspectief en de karakters van
   * dat verhaal. De karakters houden hun naam en rol (de server doet dat ook);
   * alleen hun beschrijving is te veranderen.
   */
  const kiesVoorbeeld = async (e) => {
    const id = e.target.value;
    gevraagdVoorbeeld.current = id;
    setInvoer(v => ({ ...v, voorbeeldId: id }));
    setVoorbeeld(null);
    if (!id) return;
    try {
      const detail = await api.haalTekst(id);
      if (gevraagdVoorbeeld.current !== id) return;    // intussen een ander voorbeeld gekozen
      setVoorbeeld(detail);
      const opzet = detail.opzet || {};
      setInvoer(v => ({
        ...v, sjabloon: opzet.sjabloon || v.sjabloon, perspectief: opzet.perspectief || v.perspectief,
        karakters: kopie(opzet.karakters),
      }));
    } catch (err) {
      meldFout(err.message);
    }
  };
  // Met een voorbeeld liggen naam en rol van de karakters vast; zonder karakters in het voorbeeld neemt het model ze over.
  const karaktersVast = Boolean(invoer.voorbeeldId);
  const voorbeeldZonderKarakters = Boolean(invoer.voorbeeldId && voorbeeld && !(voorbeeld.opzet?.karakters || []).length);
  // Gescheiden door komma's of puntkomma's (een plakte lijst heeft vaak het een of het ander).
  const themawoorden = invoer.themawoorden.split(/[,;\n]+/).map(w => w.trim()).filter(Boolean);
  const gekozen = sjablonen.find(s => s.nr === Number(invoer.sjabloon));
  const genoegThemawoorden = themawoorden.length >= THEMAWOORDEN[0] && themawoorden.length <= THEMAWOORDEN[1];
  // Wat er nog nodig is om te laten schrijven; staat de knop uit, dan staat dit eronder.
  const gekozenVoorbeeld = verhalen.find(v => v.id === invoer.voorbeeldId);
  const nogNodig = [
    !historisch && gekozenVoorbeeld?.genre === FICTIE_VAN_NU.value && 'een tijdvak (het voorbeeld speelt ook nu)',
    !invoer.plaats.trim() && 'de plaats',
    !invoer.situatie.trim() && 'de sociale situatie',
    !genoegThemawoorden && `${THEMAWOORDEN[0]} tot ${THEMAWOORDEN[1]} themawoorden (nu ${themawoorden.length})`,
  ].filter(Boolean);
  const kanSchrijven = nogNodig.length === 0 && !schrijven && !nakijken && (!invoer.voorbeeldId || voorbeeld);
  const karakters = invoer.karakters.filter(k => k.naam.trim())
    .map(k => ({ naam: k.naam.trim(), rol: k.rol, beschrijving: k.beschrijving.trim() }));

  const schrijf = async () => {
    if (verhaal && !window.confirm('Een nieuw verhaal vervangt het verhaal hieronder (pas bij opslaan in de database). Doorgaan?')) return;
    setSchrijven(true);
    setMelding('');
    meldFout('');
    try {
      const { voorbeeldId, karakters: _, ...rest } = invoer;
      const uit = await api.schrijfHistorischeFictie({
        ...rest, tijdvak: invoer.tijdvak.trim(), sjabloon: Number(invoer.sjabloon), themawoorden, genre,
        karakters: voorbeeldZonderKarakters ? [] : karakters, voorbeeld_id: voorbeeldId || null, tekst_id: bestaand?.id || null,
      });
      if (!uit.success) {
        meldFout(uit.message);
        return;
      }
      setVerhaal({ titel: uit.titel, beschrijving: uit.beschrijving, opzet: uit.opzet, deelteksten: uit.deelteksten,
        controle: uit.controle, genre });
      setMelding(uit.message);
      setNakijkMelding('');
    } catch (err) {
      meldFout(err.name === 'AbortError' ? 'Het schrijven duurde te lang. Probeer het opnieuw.' : err.message);
    } finally {
      setSchrijven(false);
    }
  };

  const kijkNa = async () => {
    setNakijken(true);
    setNakijkMelding('');
    meldFout('');
    try {
      const uit = await api.kijkHistorischeFictieNa(verhaal.deelteksten,
        { ...verhaal.opzet, tijdvak: invoer.tijdvak.trim(), plaats: invoer.plaats.trim() });
      if (!uit.success) {
        meldFout(uit.message);
        return;
      }
      setVerhaal(v => ({ ...v, opzet: { ...v.opzet, nakijkronde: uit.punten }, nakijkVerouderd: false }));
      setNakijkMelding(uit.message);
    } catch (err) {
      meldFout(err.name === 'AbortError' ? 'Het nakijken duurde te lang. Probeer het opnieuw.' : err.message);
    } finally {
      setNakijken(false);
    }
  };

  // Na het nakijken een deeltekst veranderen: de punten blijven staan, maar of het citaat er nog staat, is niet meer bekend.
  const zetDeel = (i, tekst) => setVerhaal(v => ({
    ...v,
    deelteksten: v.deelteksten.map((d, j) => (j === i ? tekst : d)),
    opzet: v.opzet?.nakijkronde?.length
      ? { ...v.opzet, nakijkronde: v.opzet.nakijkronde.map(p => ({ ...p, gevonden: null })) } : v.opzet,
    nakijkVerouderd: Boolean(v.opzet?.nakijkronde?.length),
  }));
  const problemen = verhaal ? verhaal.deelteksten.map((d, i) => markeringProbleem(d, i + 1)) : [];
  const anderSjabloon = verhaal?.opzet && !invoer.voorbeeldId && Number(invoer.sjabloon) !== Number(verhaal.opzet.sjabloon);
  // Het voorbeeld hoort bij het verhaal zoals het geschreven is; een ander (of geen) geldt pas na opnieuw schrijven.
  const anderVoorbeeld = verhaal?.opzet && invoer.voorbeeldId !== geschrevenNaar;
  // De karakters ook: anders ingevuld dan in het verhaal hieronder geldt pas na opnieuw schrijven.
  const naamRol = (lijst) => (lijst || []).filter(k => k.naam?.trim()).map(k => `${k.naam.trim()}|${k.rol}`).join(';');
  const andereKarakters = verhaal?.opzet && !invoer.voorbeeldId && naamRol(invoer.karakters) !== naamRol(verhaal.opzet.karakters);
  // Het perspectief hoort bij het verhaal zoals het geschreven is; een andere keuze geldt pas na opnieuw schrijven.
  const anderPerspectief = verhaal?.opzet?.perspectief && invoer.perspectief !== verhaal.opzet.perspectief;
  const kanOpslaan = verhaal && verhaal.titel.trim() && verhaal.deelteksten.length === 5 && problemen.every(p => !p)
    && !anderSjabloon && !anderVoorbeeld && !anderGenre && genoegThemawoorden && !opslaan && !nakijken;

  const bewaar = async (e) => {
    e.preventDefault();
    setOpslaan(true);
    try {
      await onOpslaan({
        titel: verhaal.titel.trim(), beschrijving: verhaal.beschrijving.trim(), text_type: 'fictioneel',
        genre: verhaal.genre,
        opzet: { ...verhaal.opzet, tijdvak: invoer.tijdvak.trim(), plaats: invoer.plaats.trim(),
          situatie: invoer.situatie.trim(), themawoorden },
        paragraphs: verhaal.deelteksten,
      });
    } finally {
      setOpslaan(false);
    }
  };

  return (
    <form onSubmit={bewaar} className="card" style={{ background: bestaand ? 'var(--blauw-vlak)' : 'var(--vlak)' }}>
      <h3 className="mb-16">
        {bestaand ? `${genreNaam(bestaand.genre).replace(/^./, c => c.toUpperCase())} bewerken — ${bestaand.titel}`
          : 'Nieuwe fictionele tekst'}
      </h3>
      {!bestaand && (
        <p className="text-small mb-16">
          Een verhaal in vijf deelteksten, met bouwstenen. Met een tijdvak en jaartal wordt het historische fictie: het
          model zoekt dan naar tijd en plaats, en je kunt de geschiedenis laten nakijken. Zonder tijdvak speelt het nu.
          {onPlakken && (
            <>
              {' '}
              <button type="button" className="btn-link" onClick={onPlakken}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer' }}>
                Of plak een bestaand verhaal
              </button>{' '}
              en laat het naar 3F omzetten (zonder deelteksten en bouwstenen).
            </>
          )}
        </p>
      )}

      <div className="grid-2">
        <div className="form-group">
          <label htmlFor="hf-tijdvak">Tijdvak en jaartal</label>
          <input id="hf-tijdvak" className="input" value={invoer.tijdvak} onChange={zet('tijdvak')}
            placeholder="leeg: het speelt nu; of bijvoorbeeld 1885, Industriële Revolutie in Nederland" />
          <small className="text-muted" style={{ display: 'block', marginTop: 4 }} data-testid="genre">
            {historisch ? 'Historische fictie.' : 'Een verhaal van nu.'}
          </small>
          {anderGenre && (
            <small style={{ display: 'block', marginTop: 4, color: 'var(--waarschuw-tekst)' }} data-testid="ander-genre">
              Het verhaal hieronder {verhaal.genre === HISTORISCHE_FICTIE.value ? 'speelt in het verleden' : 'speelt nu'}; schrijf
              het opnieuw, of {verhaal.genre === HISTORISCHE_FICTIE.value ? 'vul weer een tijdvak in' : 'maak het tijdvak leeg'}.
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="hf-plaats">Plaats en setting</label>
          <input id="hf-plaats" className="input" value={invoer.plaats} onChange={zet('plaats')}
            placeholder={historisch ? 'Amsterdam, een drukkerij aan de Nieuwezijds Voorburgwal' : 'Utrecht, een basisschool in Overvecht'} />
        </div>
        <div className="form-group">
          <label htmlFor="hf-situatie">Sociale situatie of domein</label>
          <input id="hf-situatie" className="input" value={invoer.situatie} onChange={zet('situatie')}
            placeholder={historisch ? 'werkplaats en opleiding, markt, rechtbank, huwelijk ...' : 'school, de buurt, de sportclub, thuis ...'} />
        </div>
        <div className="form-group">
          <label htmlFor="hf-themawoorden">Themawoorden (5 tot 8, gescheiden door komma's)</label>
          <input id="hf-themawoorden" className="input" value={invoer.themawoorden} onChange={zet('themawoorden')}
            placeholder={historisch ? 'het Kinderwetje van Van Houten, de stoomsnelpers, de letterzetter'
              : 'de gemeenteraad, het buurthuis, de petitie'} />
          <small style={{ color: genoegThemawoorden ? 'var(--goed-2)' : 'var(--stil)' }}>{themawoorden.length} themawoorden</small>
        </div>
        <div className="form-group">
          <label htmlFor="hf-voorbeeld">Naar het voorbeeld van (mag leeg)</label>
          <select id="hf-voorbeeld" className="input" value={invoer.voorbeeldId} onChange={kiesVoorbeeld}>
            <option value="">Geen: een nieuw verhaal</option>
            {verhalen.filter(v => v.id !== bestaand?.id
              && !(bestaand?.verwant || []).some(w => w.id === v.id && w.rol === 'versie')).map(v => (   // geen versie van zichzelf
              <option key={v.id} value={v.id}>{v.titel} ({genreNaam(v.genre)})</option>
            ))}
          </select>
          <small className="text-muted" style={{ display: 'block', marginTop: 4 }}>
            {invoer.voorbeeldId
              ? 'Een tweede versie van dat verhaal, in een andere tijd: hetzelfde plotsjabloon, dezelfde bouwstenen en karakters, en per bouwsteen wat er anders is.'
              : historisch ? 'Kies een verhaal van nu (of uit een ander tijdvak) om hetzelfde verhaal in deze tijd te laten spelen.'
                : 'Kies een historisch verhaal om hetzelfde verhaal nu te laten spelen, of kies een verhaal van nu en vul een tijdvak in.'}
          </small>
        </div>
        <div className="form-group">
          <label htmlFor="hf-sjabloon">Plotsjabloon (Propp)</label>
          <select id="hf-sjabloon" className="input" value={invoer.sjabloon} onChange={zet('sjabloon')} disabled={Boolean(invoer.voorbeeldId)}>
            {sjablonen.map(s => <option key={s.nr} value={s.nr}>{s.nr}. {s.naam}</option>)}
          </select>
          {invoer.voorbeeldId && (
            <small className="text-muted" style={{ display: 'block', marginTop: 4 }}>Het sjabloon van het voorbeeld.</small>
          )}
          {gekozen && (
            <small className="text-muted" style={{ display: 'block', marginTop: 4 }}>
              {gekozen.stappen.join(' → ')}<br />Focus: {gekozen.focus}
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="hf-perspectief">Vertelperspectief</label>
          <select id="hf-perspectief" className="input" value={invoer.perspectief} onChange={zet('perspectief')}>
            <option value="ik">{PERSPECTIEVEN.ik} (de hoofdpersoon vertelt zelf)</option>
            <option value="derde">{PERSPECTIEVEN.derde} (hij of zij, dicht bij de hoofdpersoon)</option>
          </select>
          {verhaal && !verhaal.opzet?.perspectief && (
            <small className="text-muted" style={{ display: 'block', marginTop: 4 }}>
              Bij dit verhaal is geen perspectief vastgelegd; je keuze geldt als je het opnieuw laat schrijven.
            </small>
          )}
          {anderPerspectief && (
            <small style={{ display: 'block', marginTop: 4, color: 'var(--waarschuw-tekst)' }}>
              Het verhaal hieronder is geschreven met een {PERSPECTIEVEN[verhaal.opzet.perspectief]?.toLowerCase()}; schrijf
              het opnieuw om te wisselen.
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="hf-extra">Extra wensen (mag leeg)</label>
          <input id="hf-extra" className="input" value={invoer.extra} onChange={zet('extra')}
            placeholder="bijvoorbeeld: de hoofdpersoon is een jongen; geen geweld" />
        </div>
      </div>

      <div className="form-group" data-testid="karakters">
        <label>Karakters (mag leeg; twee of drie hoofdfiguren)</label>
        {voorbeeldZonderKarakters ? (
          <p className="text-small text-muted">
            Bij het voorbeeld zijn geen karakters vastgelegd; het model neemt de namen en rollen uit het voorbeeld over.
          </p>
        ) : (
          <>
            {invoer.karakters.map((k, i) => (
              <div key={i} className="flex gap-8 mb-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <input className="input" aria-label={`Naam karakter ${i + 1}`} placeholder="naam" value={k.naam}
                  onChange={zetKarakter(i, 'naam')} disabled={karaktersVast} style={{ flex: '1 1 140px' }} />
                <select className="input" aria-label={`Rol karakter ${i + 1}`} value={k.rol} onChange={zetKarakter(i, 'rol')}
                  disabled={karaktersVast} style={{ flex: '0 0 160px' }}>
                  {KARAKTERROLLEN.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <input className="input" aria-label={`Beschrijving karakter ${i + 1}`} placeholder="leeftijd, wie het is, een eigenschap"
                  value={k.beschrijving} onChange={zetKarakter(i, 'beschrijving')} style={{ flex: '3 1 260px' }} />
                {!karaktersVast && (
                  <button type="button" className="btn btn-secondary" onClick={() => haalKarakterWeg(i)}
                    aria-label={`Karakter ${i + 1} weghalen`} style={{ padding: '6px 10px' }}><X size={14} /></button>
                )}
              </div>
            ))}
            <div className="flex gap-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              {!karaktersVast && (
                <button type="button" className="btn btn-secondary" onClick={voegKarakterToe}
                  disabled={invoer.karakters.length >= MAX_KARAKTERS}>
                  <Plus size={14} /> Karakter
                </button>
              )}
              <small className="text-muted">
                {karaktersVast ? 'De karakters van het voorbeeld: ze houden hun naam en rol, ook als een naam in deze tijd ongewoon is. De beschrijving kun je aanpassen.'
                  : 'Zonder karakters kiest het model ze zelf.'}
              </small>
            </div>
          </>
        )}
        {andereKarakters && (
          <small style={{ display: 'block', marginTop: 4, color: 'var(--waarschuw-tekst)' }}>
            Het verhaal hieronder is met andere karakters geschreven; je wijziging geldt als je het opnieuw laat schrijven.
          </small>
        )}
      </div>

      <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
        <button type="button" className="btn btn-primary" onClick={schrijf} disabled={!kanSchrijven} style={{ padding: '12px 32px' }}>
          {schrijven ? <><RefreshCw size={16} className="spinning" /> Verhaal aan het schrijven...</> : <><Sparkles size={16} /> Schrijf het verhaal</>}
        </button>
        {schrijven && (
          <p className="text-muted text-small mt-8">
            {historisch ? 'We zoeken naar tijd en plaats en schrijven het verhaal.' : 'Het model schrijft het verhaal.'} Dit kan
            een paar minuten duren...
          </p>
        )}
        {!schrijven && nogNodig.length > 0 && (
          <p className="text-small mt-8" style={{ color: 'var(--waarschuw-tekst)' }} data-testid="nog-nodig">
            Nog nodig om te schrijven: {nogNodig.join(', ')}.
          </p>
        )}
      </div>

      {melding && <div className="alert alert-success mb-16">{melding}</div>}

      {verhaal && (
        <>
          <div className="grid-2">
            <div className="form-group">
              <label htmlFor="hf-titel">Titel</label>
              <input id="hf-titel" className="input" value={verhaal.titel} onChange={e => setVerhaal({ ...verhaal, titel: e.target.value })} />
            </div>
            <div className="form-group">
              <label htmlFor="hf-beschrijving">Korte beschrijving</label>
              <input id="hf-beschrijving" className="input" value={verhaal.beschrijving}
                onChange={e => setVerhaal({ ...verhaal, beschrijving: e.target.value })} />
            </div>
          </div>

          {verhaal.controle?.waarschuwingen?.length > 0 && (
            <div className="alert mb-16" style={{ background: 'var(--waarschuw-vlak)', color: 'var(--waarschuw-tekst)' }}>
              <strong>Kijk na:</strong>
              <ul style={{ margin: '4px 0 0 20px' }}>{verhaal.controle.waarschuwingen.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}

          <p className="text-muted text-small mb-8">
            De markeringen [1A] tot en met [5B] staan vóór de zinnen met de bouwstenen. De leerling ziet ze niet. Laat ze
            staan als je een deeltekst bewerkt.
          </p>
          {verhaal.deelteksten.map((deel, i) => {
            const aantal = woordenIn(deel);
            const goedeLengte = aantal >= woordenPerDeel[0] && aantal <= woordenPerDeel[1];
            const titel = verhaal.opzet?.delen?.[i];
            return (
              <div className="form-group" key={i}>
                <div className="flex-between">
                  <label htmlFor={`hf-deel-${i + 1}`}>
                    Deeltekst {i + 1}{titel?.titel ? ` — ${titel.titel}` : ''}
                    {titel?.plotstap && <span className="text-muted" style={{ fontWeight: 'normal' }}> ({titel.plotstap})</span>}
                  </label>
                  <span style={{ fontSize: 13 }}>
                    <span style={{ color: goedeLengte ? 'var(--goed-2)' : 'var(--waarschuw-tekst)' }}>{aantal} woorden</span>
                    {problemen[i] && <span style={{ color: 'var(--fout)', marginLeft: 8 }}>{problemen[i]}</span>}
                  </span>
                </div>
                <textarea id={`hf-deel-${i + 1}`} className="input" rows={6} value={deel} spellCheck={false}
                  onChange={e => zetDeel(i, e.target.value)} style={{ fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical' }} />
              </div>
            );
          })}

          {historisch && verhaal.genre === HISTORISCHE_FICTIE.value && <div className="mb-16" data-testid="nakijken">
            <button type="button" className="btn btn-secondary" onClick={kijkNa}
              disabled={nakijken || schrijven || verhaal.deelteksten.length !== 5 || !invoer.tijdvak.trim() || !invoer.plaats.trim()}>
              {nakijken ? <><RefreshCw size={14} className="spinning" /> De geschiedenis wordt nagekeken...</>
                : <><Search size={14} /> Laat de geschiedenis nakijken</>}
            </button>
            <span className="text-muted text-small" style={{ marginLeft: 8 }}>
              {nakijken ? 'Dit duurt ongeveer een minuut.'
                : 'Een tweede ronde zoekt anachronismen in het verhaal zoals het hierboven staat. Er verandert niets in de tekst.'}
            </span>
            {nakijkMelding && <p className="text-small mt-8">{nakijkMelding}</p>}
            {verhaal.nakijkVerouderd && (
              <p className="text-small mt-8" style={{ color: 'var(--waarschuw-tekst)' }}>
                Je veranderde het verhaal na het nakijken; laat het opnieuw nakijken als je wilt dat de punten bij de tekst van nu horen.
              </p>
            )}
            <div className="mt-8"><NakijkPunten punten={verhaal.opzet?.nakijkronde} /></div>
          </div>}

          <div className="card mb-16" style={{ background: 'var(--wit)' }}>
            <Verhaalopzet opzet={verhaal.opzet} genre={verhaal.genre} verwant={bestaand?.verwant} toonNakijkronde={false} />
          </div>
        </>
      )}

      {verhaal && !genoegThemawoorden && (
        <p className="text-small mb-8" style={{ color: 'var(--fout)' }}>
          Opslaan kan met {THEMAWOORDEN[0]} tot {THEMAWOORDEN[1]} themawoorden; nu zijn het er {themawoorden.length}.
        </p>
      )}
      {anderVoorbeeld && (
        <p className="text-small mb-8" style={{ color: 'var(--fout)' }} data-testid="ander-voorbeeld">
          {geschrevenNaar
            ? `Het verhaal hieronder is geschreven naar het voorbeeld “${verhaal.opzet.voorbeeld.titel}”. `
            : 'Het verhaal hieronder is zonder voorbeeld geschreven. '}
          Schrijf het opnieuw voor {invoer.voorbeeldId ? 'het voorbeeld dat je nu koos' : 'een verhaal zonder voorbeeld'}, of kies
          weer {geschrevenNaar ? 'dat voorbeeld' : '"Geen"'}.
        </p>
      )}
      {anderSjabloon && (
        <p className="text-small mb-8" style={{ color: 'var(--fout)' }}>
          Je koos een ander plotsjabloon dan dat van dit verhaal. Schrijf het verhaal opnieuw, of kies weer
          sjabloon {verhaal.opzet.sjabloon}.
        </p>
      )}
      <div className="flex gap-8">
        <button type="submit" className="btn btn-success" disabled={!kanOpslaan}>
          <Check size={16} /> Opslaan en sluiten
        </button>
        <button type="button" className="btn btn-secondary" onClick={onAnnuleer}>Annuleren</button>
      </div>
    </form>
  );
}
